/**
 * Lightweight IndexedDB session store.
 *
 * Stored on login, cleared on logout, read on every route transition by
 * `<RequireAuth>`. Survives localStorage eviction and works in strict
 * private-browsing modes where localStorage throws on write.
 *
 * Why IndexedDB over localStorage:
 *   - localStorage is sync and quota-restricted; in private mode it
 *     throws. IndexedDB degrades more gracefully.
 *   - A Sentinel operator console may run multi-hour sessions with
 *     attestation payloads cached alongside the session — IndexedDB
 *     is the right shape for that future.
 *
 * This file deliberately does NOT introduce `idb` / `idb-keyval` as a
 * dep — the native API is small enough inline.
 */

const DB_NAME = 'sentinel';
const DB_VERSION = 1;
const STORE = 'session';
const SESSION_KEY = 'current';

export interface StoredSession {
  /** Email the operator signed in with. */
  email: string;
  /** Role granted by the backend demo token. */
  role: string;
  /** Opaque bearer token from `POST /api/v1/auth/signin`. Demo only. */
  token: string;
  /** ISO-8601 timestamp the session was created. */
  createdAt: string;
  /** ISO-8601 timestamp the session is considered invalid. */
  expiresAt: string;
  /**
   * Whether the session was created via the self-serve "Create account"
   * flow rather than a plain sign-in. Surfaced in the audit trail.
   */
  createdVia: 'signin' | 'signup';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function runTx<T>(
  mode: IDBTransactionMode,
  cb: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = cb(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => db.close();
    tx.onabort = () => db.close();
  });
}

export async function putSession(s: StoredSession): Promise<void> {
  try {
    await runTx<IDBValidKey>('readwrite', (store) => store.put(s, SESSION_KEY));
  } catch (err) {
    // IndexedDB can be unavailable in some embedded / private contexts.
    // Silently fall back to a memory cache so the UI still works for the
    // current tab — the user will just need to sign in again on next
    // load. Also mirror to localStorage as a belt-and-braces fallback
    // (this is the 2nd-line cache the previous iteration used).
    console.warn('[session] IndexedDB putSession failed, falling back to memory', err);
    memoryFallback = s;
    try {
      window.localStorage.setItem('sentinel.session.v1', JSON.stringify(s));
    } catch {
      // storage blocked — the memory fallback above still covers this tab.
    }
  }
  notifySessionSubscribers(s);
}

export async function getSession(): Promise<StoredSession | null> {
  // If a prior putSession succeeded via IDB this returns the stored row;
  // otherwise try the two fallbacks in order.
  try {
    const row = await runTx<StoredSession | undefined>('readonly', (store) =>
      store.get(SESSION_KEY) as IDBRequest<StoredSession | undefined>,
    );
    if (row) return isExpired(row) ? null : row;
  } catch {
    // fall through to alternatives
  }
  if (memoryFallback && !isExpired(memoryFallback)) return memoryFallback;
  try {
    const raw = window.localStorage.getItem('sentinel.session.v1');
    if (raw) {
      const parsed = JSON.parse(raw) as StoredSession;
      return isExpired(parsed) ? null : parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

export async function clearSession(): Promise<void> {
  memoryFallback = null;
  try {
    window.localStorage.removeItem('sentinel.session.v1');
  } catch {
    // ignore
  }
  try {
    await runTx<undefined>('readwrite', (store) => store.delete(SESSION_KEY));
  } catch {
    // If we couldn't reach IDB the memory + localStorage clear above is
    // enough to log the user out of the current tab.
  }
  notifySessionSubscribers(null);
}

function isExpired(s: StoredSession): boolean {
  const exp = Date.parse(s.expiresAt);
  if (Number.isNaN(exp)) return false;
  return exp <= Date.now();
}

let memoryFallback: StoredSession | null = null;

/** 8 hours — matches the backend demo-token TTL (ADR-0010). */
export const SESSION_TTL_MS = 8 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Subscription API (Blocker-1 fix from docs/review/deep-review-8f9dc7a.md).
//
// `apiClient.apiFetch` wants the latest session synchronously on every
// outgoing request so it can attach `Authorization: Bearer …`. We keep a
// module-local `currentSession` cache that `putSession` / `clearSession`
// push to, and `subscribeSession` lets the client hook into changes so it
// never attaches a stale token after sign-out.
// ---------------------------------------------------------------------------

let currentSession: StoredSession | null = null;
const sessionSubscribers = new Set<(s: StoredSession | null) => void>();

function notifySessionSubscribers(s: StoredSession | null): void {
  currentSession = s;
  for (const fn of sessionSubscribers) {
    try {
      fn(s);
    } catch (err) {
      console.warn('[session] subscriber threw', err);
    }
  }
}

/**
 * Synchronous accessor for the **most recently observed** session. Safe
 * to call from inside `fetch` wrappers; returns `null` until the first
 * `putSession` / `refreshCachedSession` call completes for this tab.
 */
export function peekSession(): StoredSession | null {
  return currentSession;
}

/**
 * Register `listener` for session changes. The listener fires with the
 * fresh session on `putSession`, and with `null` on `clearSession`. The
 * returned callback unsubscribes.
 */
export function subscribeSession(
  listener: (s: StoredSession | null) => void,
): () => void {
  sessionSubscribers.add(listener);
  // Emit the current value so callers don't have to branch on
  // "haven't-heard-from-you-yet" themselves.
  try {
    listener(currentSession);
  } catch (err) {
    console.warn('[session] subscriber threw on initial emit', err);
  }
  return () => {
    sessionSubscribers.delete(listener);
  };
}

/**
 * Refresh the synchronous cache by re-reading the async store. Call
 * from `<RequireAuth>` / app boot so `peekSession` returns the current
 * row once IDB has responded.
 */
export async function refreshCachedSession(): Promise<StoredSession | null> {
  const s = await getSession();
  notifySessionSubscribers(s);
  return s;
}
